/**
 * PadRedoArchiveBanner
 *
 * Quiet, persistent note shown after a SLaM Post-Assessment Discussion has been
 * redone because the formal Diagnostic Decision disagreed with the outcome the
 * original discussion was held under.
 *
 * Why this exists separately from the amber "Outcome changed" staleness cue:
 * that cue only fires while the *live* completed PAD is still out of date with
 * the DD. Once the Lead redoes the discussion, staleness clears — but clinicians
 * still need a way to open the archived original (and a visible reminder that a
 * prior discussion was superseded). Without this banner the overview / PAD
 * pages look like a never-redone case, and "View original" disappears with the
 * staleness cue.
 *
 * Visual style matches SSIOverrideBanner (amber, left-aligned) so the two
 * "something superseded this module" surfaces read as the same family of note.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ExclamationIcon } from '@heroicons/react/solid';

const OUTCOME_LABEL = {
  positive: 'a positive ADHD outcome',
  negative: 'a non-diagnosis outcome',
};

/**
 * @param {Object} props
 * @param {boolean} props.isVisible
 * @param {string|null} [props.snapshotPath] - Route to pad-snapshot viewer
 * @param {string|null} [props.redoFromOutcome] - 'positive' | 'negative' | null
 */
const PadRedoArchiveBanner = ({
  isVisible = false,
  snapshotPath = null,
  redoFromOutcome = null,
}) => {
  const fromLabel = OUTCOME_LABEL[redoFromOutcome] || 'a previous outcome';

  return (
    <div
      className={`overflow-hidden transition-all duration-500 ease-in-out ${
        isVisible ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}
    >
      <div className="bg-amber-50 rounded-2xl p-4 text-left">
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-1">
            <ExclamationIcon className="h-5 w-5 text-amber-600" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <h3 className="text-md font-medium text-amber-800">
              Previous Post-Assessment Discussion archived
            </h3>
            <div className="mt-2 text-sm text-amber-700">
              <p className="mb-2">
                This Post-Assessment Discussion was restarted after the formal
                diagnosis changed. The original discussion (held for{' '}
                <strong>{fromLabel}</strong>) is kept as a read-only archive.
              </p>
              {snapshotPath && (
                <p>
                  <Link
                    to={snapshotPath}
                    className="font-semibold text-amber-800 underline hover:text-amber-900"
                  >
                    View original
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PadRedoArchiveBanner;
